import ExcelJS from "exceljs";
import { getGroup360ResponsesExport } from "../axios/axiosFunctions";
import { interpolateReceiverName } from "./questionText";

const ROLE_LABELS = {
  manager: "Manager",
  direct: "Direct Report",
  peer: "Peer",
  otherColleague: "Other Colleague",
};

// Excel sheet names: max 31 chars, no \ / * ? : [ ]
const sanitizeSheetName = (name) =>
  name.replace(/[\\/*?:[\]]/g, " ").replace(/\s+/g, " ").trim().slice(0, 31) ||
  "Receiver";

/**
 * Download every provider's answers for a Simple 360 Feedback team as an
 * XLSX workbook: one worksheet per feedback receiver, one row per provider
 * (attributed by name), one column per question.
 */
export const downloadSimple360Responses = async (groupId, slug, groupName) => {
  const data = await getGroup360ResponsesExport(groupId, slug);
  if (!data || !Array.isArray(data.receivers)) return false;

  const workbook = new ExcelJS.Workbook();
  workbook.creator = "K2G Assessments";
  workbook.created = new Date();

  const usedNames = new Set();
  for (const receiver of data.receivers) {
    const fullName =
      `${receiver.firstName || ""} ${receiver.lastName || ""}`.trim() ||
      receiver.email;

    let sheetName = sanitizeSheetName(fullName);
    let n = 2;
    while (usedNames.has(sheetName.toLowerCase())) {
      sheetName = sanitizeSheetName(`${fullName.slice(0, 25)} (${n})`);
      n++;
    }
    usedNames.add(sheetName.toLowerCase());
    const ws = workbook.addWorksheet(sheetName);

    const questions = data.questions || [];
    ws.columns = [
      { width: 16 },
      { width: 16 },
      { width: 30 },
      { width: 16 },
      { width: 14 },
      ...questions.map(() => ({ width: 50 })),
    ];

    const titleRow = ws.addRow([`Feedback for ${fullName}`]);
    titleRow.font = { bold: true, size: 14 };
    ws.mergeCells(1, 1, 1, 5 + questions.length);
    ws.addRow([]);

    const headerRow = ws.addRow([
      "First Name",
      "Last Name",
      "Email",
      "Role",
      "Status",
      ...questions.map((q) => interpolateReceiverName(q.text, fullName)),
    ]);
    headerRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
    headerRow.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF0F5385" },
    };
    headerRow.alignment = { wrapText: true, vertical: "top" };
    headerRow.height = 60;

    for (const p of receiver.providers || []) {
      const row = ws.addRow([
        p.firstName,
        p.lastName,
        p.email,
        ROLE_LABELS[p.role] || p.role || "",
        p.status,
        ...questions.map((q) => p.answers?.[String(q._id)] || ""),
      ]);
      row.alignment = { wrapText: true, vertical: "top" };
    }
  }

  if (workbook.worksheets.length === 0) {
    workbook.addWorksheet("No receivers");
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  const safeTeam = (groupName || "team").replace(/[^a-z0-9]+/gi, "_");
  const date = new Date().toISOString().split("T")[0];
  link.setAttribute("href", url);
  link.setAttribute("download", `simple360_responses_${safeTeam}_${date}.xlsx`);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  return true;
};
